import { Clock } from 'lucide-react';
import { useExpiringMembers } from '../../queries';

const NULL_SYMBOL = '—';

const initials = (name: string | null | undefined): string => {
  const safe = String(name || '').trim();
  if (!safe) return '?';
  const parts = safe.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || safe[0]!.toUpperCase();
};

const formatDaysUntil = (iso: string | null | undefined): string => {
  if (!iso) return NULL_SYMBOL;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return NULL_SYMBOL;
  const days = Math.ceil((parsed.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'expired';
  if (days === 0) return 'today';
  if (days === 1) return 'in 1 day';
  return `in ${days} days`;
};

const PointsExpiringPanel = () => {
  // §12 of the brief: expiring window is fixed at 30 days in Stage 1.
  const query = useExpiringMembers(30, true);
  const rows = query.data ?? [];
  const totalAtRisk = rows.reduce((sum, r) => sum + (r.pointsAtRisk ?? 0), 0);

  return (
    <article className="actions-panel" data-testid="points-expiring-panel">
      <header className="actions-panel__header">
        <span className="actions-panel__icon" aria-hidden="true">
          <Clock size={16} />
        </span>
        <div className="actions-panel__title-block">
          <h3 className="actions-panel__title">Points expiring soon</h3>
        </div>
        {rows.length > 0 ? (
          <span className="actions-panel__suffix actions-panel__suffix--warn">
            <strong>{totalAtRisk.toLocaleString('en-IN')}</strong> pts at risk
          </span>
        ) : null}
      </header>

      {query.isLoading ? (
        <div className="actions-panel__loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="actions-panel__empty">No points expiring in the next 30 days.</div>
      ) : (
        <ul className="actions-panel__list">
          {rows.map((row, index) => (
            <li
              key={row.membershipUuid || `e-${index}`}
              className="actions-panel__item"
              data-testid={`points-expiring-${index}`}
            >
              <span className="actions-panel__avatar" aria-hidden="true">
                {initials(row.customerName)}
              </span>
              <div className="actions-panel__item-body">
                <div className="actions-panel__item-name">{row.customerName || NULL_SYMBOL}</div>
                <div className="actions-panel__item-sub">{formatDaysUntil(row.expiresAt)}</div>
              </div>
              <div className="actions-panel__item-num actions-panel__item-num--warn">
                <span className="actions-panel__item-num-value">
                  {Number(row.pointsAtRisk ?? 0).toLocaleString('en-IN')}
                </span>
                <span className="actions-panel__item-num-unit">pts</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};

export default PointsExpiringPanel;
